import { useState } from "react";

import { useTaskDetailDrawer, type TaskDrawerIdentity } from "./filters";
import type { Task } from "./types";

/**
 * Which task the detail drawer is open on, as the two tabs both need it.
 *
 * Two facts, not one, because they live in two places and neither can be
 * derived from the other. The *identity* is in the URL (`useTaskDetailDrawer`)
 * so a reload or a paste reopens the same drawer; the *row* is in component
 * state, because the drawer draws the task's own columns - status, selector,
 * progress - and only the listing that was on screen holds them. Neither tab
 * can look a task up from an identity alone: the plans tab reads tasks per
 * open plan card, and the All tasks tab reads only released ones.
 *
 * Shared between both tabs rather than written twice, because reconciling the
 * two (`taskDrawerSelection`) is the part that is easy to get subtly wrong, and
 * a tab that skipped it would render the previous task's rows under the new
 * task's URL.
 */
/** The drawer as it is open, once the URL and the held row have been agreed. */
export interface TaskDrawerSelection {
  identity: TaskDrawerIdentity;
  task: Task;
}

/**
 * The two halves reconciled: open only when both are present and name the same
 * task.
 *
 * A pure function rather than inlined in the hook below, so the check most
 * likely to be gotten subtly wrong is unit-testable without a router - the same
 * reason `groupRequirementsByPick` is pulled out of `useTaskDetail`.
 *
 * The URL is what a reload or a paste restores, and on that first render there
 * is no row in hand at all - so a drawer keyed on the URL alone would open over
 * nothing, and one keyed on the held row alone would stay open after the URL was
 * cleared by a back button. Requiring them to agree makes a cold load read as
 * closed, which is the honest answer until a task can be resolved from its
 * identity.
 *
 * A warehouse-scoped task read that spans unreleased plans now exists -
 * `GET /{orgId}/tasks`, which `plannedTasksInit` and `taskPlanLookupInit` in
 * `use-plans.ts` both read since this change - so the read this cold-load
 * restore would need is no longer missing. Restoring is still deferred, though:
 * it needs a lookup by identity (`planId` + `taskId` from the URL) rather than
 * by filters, two queries rather than one (the plans tab does not know which
 * world a URL's task lives in until one of them answers), and a third
 * selection state in `taskDrawerSelection` for "resolving" between "closed" and
 * "open". That is its own change, deliberately not folded into this one.
 */
export function taskDrawerSelection(
  identity: TaskDrawerIdentity | null,
  task: Task | null,
): TaskDrawerSelection | null {
  return identity !== null &&
    task !== null &&
    task.planId === identity.planId &&
    // The row's id is the warehouse-scoped number said as a string; the URL
    // carries it as a number, because that is what both endpoints take.
    Number(task.id) === identity.taskId
    ? { identity, task }
    : null;
}

export function useTaskDrawerSelection(): {
  /** The open drawer, or null - both halves present and naming the same task. */
  selected: TaskDrawerSelection | null;
  /** Open the drawer on a clicked row. */
  select: (task: Task) => void;
  close: () => void;
} {
  const drawer = useTaskDetailDrawer();
  const [task, setTask] = useState<Task | null>(null);

  return {
    selected: taskDrawerSelection(drawer.identity, task),
    select: (next) => {
      setTask(next);
      drawer.open({ planId: next.planId, taskId: Number(next.id) });
    },
    close: () => {
      drawer.close();
      setTask(null);
    },
  };
}
